import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ChangeReply, ReassignSuggestion, Ticket } from '../api/types.js';
import { useDecideTicket, useTicketAssignees, useTicketSuggestions, useTickets, type TicketDecision } from '../hooks/use-tickets.js';
import { parseIssueRef, ticketLabel, ticketBranch, trackerWords } from '../api/pr-ref.js';
import { timeAgo } from '../utils/time-ago.js';
import { Button } from './button.js';
import { BreakdownForm, ConsistencyCheck, EpicPlanView, EpicProgressView, InertMentions, LeftOutNotes, Part, Questions, RelatedPlans, SubTicketResults } from './ticket-epic.js';
import { PlainText } from './plain-text.js';
import { ChangeErrors, ChangeView, feedbackByThread, feedbackPlace } from './ticket-change.js';
import { TicketAuthors, TrustedPeople } from './ticket-authors.js';
import { authorsToTrust } from '../api/ticket-trust.js';

// « Tickets »: GitHub issues assigned to the operator, each with the plan its session prepared (ADR-026 step 1). The
// operator implements, hands over or dismisses; nothing reaches GitHub before. A reply comments without deciding: the
// plan and its actions stay, the reply goes to the card's history (and may ask the session for an updated plan). An epic (section 5) gets a
// breakdown to edit and create as sub-tickets, then its progress. Everything the session wrote read text from others:
// shown as plain text, never as Markdown or HTML (plain-text.tsx keeps its lists, paragraphs and `code`).
//
// A card reads in two columns when it is wide enough (a container query, so the sidebar does not squeeze the plan): the plan at a reading measure on the left, with the form an action opens
// under it; the decision rail on the right (actions, files, related plans, who and when, the session), sticky while the
// plan scrolls. On narrow screens one column, the actions last.
//
// After Implement (step 2) the same card carries the change: the session's commit as the API read it (files, diff,
// tests) under the plan, and the decision on it in the rail: Open the PR (title, description, draft), Ask for changes
// (a note back to the session), Drop. After feedback on its pull request (ADR-035), the change may carry replies to the
// comments: the operator edits them, posts the ones he keeps with the update (Push the update) or alone (Post the replies).

type Mode = 'implement' | 'reassign' | 'reply' | 'sub-tickets' | 'open-pr' | 'changes' | 'replies';
type Titles = Map<string, string | undefined>;

const field = 'block w-full rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus-visible:outline focus-visible:outline-2';
const fieldStyle = { backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)', color: 'var(--color-text)' };
const MAX_TEXT = 20000;

function Plan({ t }: { t: Ticket }) {
  const p = t.proposal;
  if (!p) {
    return (
      <p className="rounded-md px-3 py-2 text-sm" style={{ backgroundColor: 'var(--color-error-dim)', color: 'var(--color-error)' }}>
        The session&apos;s plan was refused by the checks: {t.errors.join('; ')}. A new event on the ticket brings a new plan; reject this one meanwhile.
      </p>
    );
  }
  return (
    <div className="space-y-5">
      <Part title="Summary"><PlainText text={p.summary} /></Part>
      <Part title="Proposed approach"><PlainText text={p.approach} /></Part>
      <Questions questions={p.questions} />
      <LeftOutNotes t={t} />
      <InertMentions t={t} />
    </div>
  );
}

/** The paths the plan expects to touch, one per line in the rail */
function Files({ files }: { files: string[] }) {
  if (!files.length) return null;
  return (
    <Part title={files.length === 1 ? 'File it expects to change' : `Files it expects to change (${files.length})`}>
      <ul className="space-y-1 text-xs leading-snug" style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text-2)' }}>
        {files.map((f) => <li key={f} className="min-w-0 break-all">{f}</li>)}
      </ul>
    </Part>
  );
}

/** A second way to submit the same form: which one was pressed, and its own pending label */
interface AltSubmit { label: string; pendingLabel: string; active: boolean; onPick: (alt: boolean) => void }

/** What the API will do, said before the operator confirms */
function Form({ label, hint, value, onChange, submit, pendingLabel, pending, valid, onCancel, children, autoFocus = true, placeholder, alt }: {
  label: string; hint: string; value: string; onChange: (v: string) => void; submit: string; pendingLabel: string; pending: boolean; valid: boolean;
  onCancel: () => void; children?: React.ReactNode; autoFocus?: boolean; placeholder?: string; alt?: AltSubmit;
}) {
  return (
    <>
      <p className="max-w-[72ch] text-xs" style={{ color: 'var(--color-text-2)' }}>{hint}</p>
      {children}
      <label className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
        {label}
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={7} maxLength={MAX_TEXT} autoFocus={autoFocus} placeholder={placeholder}
          className={`${field} mt-1 font-[inherit] leading-relaxed`} style={fieldStyle} />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" className="max-lg:min-h-10" disabled={!valid || pending} onClick={alt ? () => alt.onPick(false) : undefined}>
          {pending && !alt?.active ? pendingLabel : submit}
        </Button>
        {alt && (
          <Button type="submit" size="sm" variant="ghost" className="max-lg:min-h-10" disabled={!valid || pending} onClick={() => alt.onPick(true)}>
            {pending && alt.active ? alt.pendingLabel : alt.label}
          </Button>
        )}
        <Button type="button" size="sm" variant="ghost" className="max-lg:min-h-10" onClick={onCancel} disabled={pending}>Cancel</Button>
      </div>
    </>
  );
}

type Assignees = ReturnType<typeof useTicketAssignees>;

/** Who aidev suggests (ADR-030 step 3), one click to pick; the list below stays for anyone else */
function Suggestions({ suggestions, value, onChange }: { suggestions: ReassignSuggestion[]; value: string; onChange: (v: string) => void }) {
  if (!suggestions.length) return null;
  return (
    <div className="mb-3" role="group" aria-label="Suggested teammates">
      <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>Suggested from what aidev knows of them</p>
      <div className="mt-1 flex flex-wrap gap-2">
        {suggestions.map((s) => {
          const picked = value.toLowerCase() === s.login.toLowerCase();
          return (
            <button key={s.login} type="button" aria-pressed={picked} onClick={() => onChange(s.login)}
              className="rounded-md px-2.5 py-1.5 text-left text-xs focus-visible:outline focus-visible:outline-2 max-lg:min-h-10"
              style={{
                border: `1px solid ${picked ? 'var(--color-brand)' : 'var(--color-border-2)'}`,
                backgroundColor: picked ? 'var(--color-brand-dim)' : 'var(--color-surface)',
                outlineColor: 'var(--color-brand)',
              }}>
              <span className="block font-medium" style={{ color: 'var(--color-text)' }}>{s.login}</span>
              <span className="block" style={{ color: 'var(--color-text-3)' }}>{s.reason}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Assignee({ assignees, suggestions = [], value, onChange }: { assignees: Assignees; suggestions?: ReassignSuggestion[]; value: string; onChange: (v: string) => void }) {
  const { data, isLoading } = assignees;
  return (
    <>
      <Suggestions suggestions={suggestions} value={value} onChange={onChange} />
      <label className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
        Teammate
        <select value={value} onChange={(e) => onChange(e.target.value)} disabled={isLoading}
          className={`${field} mt-1 sm:max-w-sm max-lg:min-h-10`} style={fieldStyle} autoFocus>
          <option value="">{isLoading ? 'Loading who can be assigned…' : 'Pick a teammate'}</option>
          {(data ?? []).map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </label>
    </>
  );
}

/** Once seen, stays seen: the assignable people are read for the cards the operator looks at */
function useSeen(ref: React.RefObject<HTMLElement | null>, skip: boolean): boolean {
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (seen || skip || !ref.current || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) setSeen(true); }, { rootMargin: '200px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, seen, skip]);
  return seen;
}

interface ReplyDraft extends ChangeReply { keep: boolean }
interface Drafts { approach: string; handover: string; reply: string; login: string; title: string; body: string; draft: boolean; note: string; replies: ReplyDraft[] }

/** The change's replies still to post, as the operator edits them */
const replyDrafts = (t: Ticket): ReplyDraft[] => {
  const posted = new Set((t.repliesPosted ?? []).map((r) => r.to));
  return (t.change?.replies ?? []).filter((r) => !posted.has(r.to)).map((r) => ({ ...r, keep: true }));
};
const keptReplies = (replies: ReplyDraft[]): ChangeReply[] => replies.filter((r) => r.keep && r.body.trim()).map(({ to, body }) => ({ to, body: body.trim() }));

/** The decision: its buttons go in the rail, the form an action opens goes under the plan */
function useDecision(serverId: string, t: Ticket, canDecide: boolean, seen: boolean, onDecided: (note?: string) => void) {
  const decide = useDecideTicket(serverId, t.externalRef);
  const assignees = useTicketAssignees(serverId, t.externalRef, canDecide && seen);
  const suggestions = useTicketSuggestions(serverId, t.externalRef, canDecide && seen);
  const [mode, setMode] = useState<Mode | null>(null);
  const plan = t.kind === 'epic' ? t.epic : t.proposal;
  const [drafts, setDrafts] = useState<Drafts>(() => ({
    approach: t.proposal?.approach ?? '', handover: plan?.handoverDraft ?? '', reply: plan?.replyDraft ?? '', login: t.reassignedTo ?? '',
    title: t.change?.title ?? '', body: t.change?.body ?? '', note: '', replies: replyDrafts(t),
    // A change hard to judge here (cut diff, failed tests) opens as a draft unless the operator says otherwise
    draft: !!t.change && (t.change.diffTruncated || t.change.tests.some((r) => r.passed === false)),
  }));
  const go = (d: TicketDecision) => decide.mutate(d, {
    onSuccess: (r) => {
      // A reply is not a decision: its form closes, the plan and its actions stay
      if (d.decision === 'reply') { setMode(null); setDrafts((x) => ({ ...x, reply: '' })); }
      if (d.decision === 'request-changes') { setMode(null); setDrafts((x) => ({ ...x, note: '' })); }
      if (d.decision === 'post-replies') { setMode(null); setDrafts((x) => ({ ...x, replies: replyDrafts(r.ticket) })); }
      onDecided(r.note);
    },
  });
  const cancel = () => { setMode(null); decide.reset(); };
  const open = (m: Mode) => { decide.reset(); setMode(mode === m ? null : m); };
  // The list can come back empty or fail after Reassign was opened: the rail then says why, no dead-end form stays
  const reassignable = !assignees.isError && assignees.data?.length !== 0;
  return { decide, assignees, suggestions: suggestions.data ?? [], mode: mode === 'reassign' && !reassignable ? null : mode, open, cancel, go, drafts, set: (patch: Partial<Drafts>) => setDrafts((d) => ({ ...d, ...patch })) };
}
type Decision = ReturnType<typeof useDecision>;

const formId = (t: Ticket) => `ticket-${t.externalRef}-decision`;

function DecideError({ d }: { d: Decision }) {
  const error = d.decide.error as Error | null;
  return error ? <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Not done: {error.message}</p> : null;
}

/** The change's actions, in the rail: Open the PR first when there is a change to open, then Ask for changes, Drop */
function ChangeActions({ t, d }: { t: Ticket; d: Decision }) {
  const openPr = t.pullRequest && !t.pullRequest.closedAt;
  const toggle = (m: Mode) => ({ onClick: () => d.open(m), 'aria-expanded': d.mode === m, 'aria-controls': d.mode === m ? formId(t) : undefined });
  const touch = 'max-lg:min-h-10';
  const w = trackerWords(t.externalRef);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2" role="group" aria-label={`Decide on the change of ${t.externalRef.replace(/^git(hub|lab):/, '')}`}>
        {t.status === 'change-ready' && <Button size="sm" className={`${touch} w-full @max-3xl:sm:w-auto`} {...toggle('open-pr')}>{openPr ? 'Push the update' : `Open the ${w.short}`}</Button>}
        {t.status === 'change-ready' && openPr && d.drafts.replies.length > 0 && <Button size="sm" variant="ghost" className={touch} {...toggle('replies')}>Post the replies</Button>}
        <Button size="sm" variant="ghost" className={touch} {...toggle('changes')}>Ask for changes</Button>
        <Button size="sm" variant="ghost" className={touch} disabled={d.decide.isPending} onClick={() => d.go({ decision: 'drop' })}>Drop</Button>
      </div>
      {d.mode === null && <DecideError d={d} />}
    </div>
  );
}

/** The actions, in the rail: Implement first for a ticket, Create the sub-tickets for an epic */
function Actions({ t, d, actionable }: { t: Ticket; d: Decision; actionable: boolean }) {
  const epic = t.kind === 'epic';
  const pending = d.decide.isPending;
  const { data: people, isError, error } = d.assignees;
  const issue = parseIssueRef(t.externalRef);
  const reasonId = `ticket-${t.externalRef}-reassign-why`;
  const noOne = !!people && people.length === 0;
  const why = isError ? `Who can be assigned could not be read: ${(error as Error).message}`
    : noOne ? `Nobody else can be assigned on ${issue ? `${issue.org}/${issue.repo}` : 'this repository'}.` : null;
  const toggle = (m: Mode) => ({ onClick: () => d.open(m), 'aria-expanded': d.mode === m, 'aria-controls': d.mode === m ? formId(t) : undefined });
  const touch = 'max-lg:min-h-10';
  // A GitLab ticket (ADR-041 step 3): implemented, replied to or rejected, never reassigned from aidev
  const gitlab = !!issue?.gitlab;
  const primary = actionable && (epic ? (t.epic?.subTickets.length ?? 0) > 0 : true);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2" role="group" aria-label={`Decide on ${t.externalRef.replace(/^github:/, '')}`}>
        {primary && (
          <Button size="sm" className={`${touch} w-full @max-3xl:sm:w-auto`} {...toggle(epic ? 'sub-tickets' : 'implement')}>
            {epic ? 'Create the sub-tickets' : 'Implement'}
          </Button>
        )}
        {actionable && (
          <>
            {!gitlab && <Button size="sm" variant="ghost" className={touch} disabled={!!why} aria-describedby={why ? reasonId : undefined} {...toggle('reassign')}>Reassign</Button>}
            <Button size="sm" variant="ghost" className={touch} {...toggle('reply')}>Reply</Button>
          </>
        )}
        <Button size="sm" variant="ghost" className={touch} disabled={pending} onClick={() => d.go({ decision: 'dismiss' })}>Reject</Button>
      </div>
      {actionable && !gitlab && why && <p id={reasonId} className="text-xs" style={{ color: 'var(--color-text-3)' }}>Reassign is unavailable. {why}</p>}
      {d.mode === null && <DecideError d={d} />}
    </div>
  );
}

/** The form the chosen action opens, under the plan */
function DecisionForm({ t, d }: { t: Ticket; d: Decision }) {
  const { mode, drafts, set, cancel } = d;
  const epic = t.kind === 'epic';
  const pending = d.decide.isPending;
  // Which reply button was pressed: set on its click, read by the submit that follows
  const updatePlan = useRef(false);
  // Implement: trusting the ticket's authors, or from the plan only (ADR-029), the same way
  const planOnly = useRef(false);
  if (mode === null) return null;
  const trust = authorsToTrust(t);
  const offerTrust = trust.logins.length > 0;
  const sent = d.decide.variables;
  const updating = sent?.decision === 'reply' && sent.updatePlan === true;
  const gitlabTicket = !!parseIssueRef(t.externalRef)?.gitlab;
  const w = trackerWords(t.externalRef);
  function submit(e: FormEvent) {
    e.preventDefault();
    if (mode === 'implement') d.go({ decision: 'implement', approach: drafts.approach.trim(), ...(offerTrust && !planOnly.current ? { trustAuthors: trust.logins } : {}) });
    if (mode === 'reassign') d.go({ decision: 'reassign', login: drafts.login, comment: drafts.handover.trim() });
    if (mode === 'reply') d.go({ decision: 'reply', comment: drafts.reply.trim(), ...(updatePlan.current ? { updatePlan: true } : {}) });
    if (mode === 'open-pr' && t.change) {
      const replies = openPr ? keptReplies(drafts.replies) : [];
      d.go({ decision: 'open-pr', commit: t.change.commit, title: drafts.title.trim(), body: drafts.body.trim(), draft: drafts.draft, ...(replies.length ? { replies } : {}) });
    }
    if (mode === 'replies') d.go({ decision: 'post-replies', replies: keptReplies(drafts.replies) });
    if (mode === 'changes') d.go({ decision: 'request-changes', note: drafts.note.trim() });
  }
  const base = t.change?.baseBranch ?? 'the default branch';
  // A change made after feedback on its pull request, still open, goes onto that pull request (ADR-035 step 2)
  const openPr = t.pullRequest && !t.pullRequest.closedAt ? t.pullRequest : undefined;
  return (
    <div id={formId(t)} className="space-y-2">
      {mode === 'sub-tickets' ? (
        <BreakdownForm t={t} pending={pending} onCancel={cancel} onSubmit={(subTickets) => d.go({ decision: 'sub-tickets', subTickets })} />
      ) : (
        <form onSubmit={submit} className="space-y-3 rounded-md border p-3" style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface-2)' }}
          aria-label={mode === 'implement' ? 'Implement the plan' : mode === 'reassign' ? `Reassign the ${epic ? 'epic' : 'ticket'}`
            : mode === 'open-pr' ? `Open the ${w.request}` : mode === 'changes' ? 'Ask for changes' : mode === 'replies' ? 'Post the replies' : `Reply on the ${epic ? 'epic' : 'ticket'}`}>
          {mode === 'implement' && (
            <Form label="Approach" value={drafts.approach} onChange={(v) => set({ approach: v })} pending={pending} valid={!!drafts.approach.trim()} onCancel={cancel}
              submit={offerTrust ? 'Implement, trusting these authors' : 'Implement this approach'} pendingLabel="Sending the approach…"
              alt={offerTrust ? { label: 'Implement from the plan only', pendingLabel: 'Sending the approach…', active: sent?.decision === 'implement' && !sent.trustAuthors, onPick: (alt) => { planOnly.current = alt; } } : undefined}
              hint={`A new session, with your tools and the network, works on a local branch and runs the tests${gitlabTicket ? ', from this approach alone: it does not read the issue' : ''}. Nothing is pushed and no ${w.request} is opened: its change comes back here for you to review first.`}>
              {trust.known && (
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>Who wrote in the ticket</p>
                  <TicketAuthors authors={t.authors!} />
                  <p className="max-w-[72ch] text-xs" style={{ color: 'var(--color-text-3)' }}>
                    {offerTrust
                      ? `Trusting ${trust.logins.length === 1 ? trust.logins[0] : 'them'} adds ${trust.logins.length === 1 ? 'this account' : 'these accounts'} to the people you trust on this server, and the session may read the ticket. From the plan only, it gets your approach alone and does not read the ticket.`
                      : trust.untrusted.length ? 'A bot wrote in the ticket: it is implemented from the plan only.' : 'You trust everyone who wrote in the ticket.'}
                  </p>
                </div>
              )}
            </Form>
          )}
          {mode === 'open-pr' && (
            <>
              <p className="max-w-[72ch] text-xs" style={{ color: 'var(--color-text-2)' }}>
                {openPr ? (
                  <>aidev reads the commit again and checks it is the change shown here, then pushes it onto <code>{openPr.branch}</code> of{' '}
                    <a href={openPr.url} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{w.sigil}{openPr.number}</a>{' '}
                    under your name, as one commit: the branch then holds exactly this change. The session itself pushes nothing.</>
                ) : (
                  <>aidev reads the commit again and checks it is the change shown here, then creates <code className="break-all">{ticketBranch(t.externalRef, drafts.title, t.step)}</code> on {w.site},
                    commits the files under your name and opens the {w.request} to <code>{base}</code>. The session itself pushes nothing.</>
                )}
              </p>
              <label className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
                {openPr ? 'Commit headline' : 'Title'}
                <input value={drafts.title} onChange={(e) => set({ title: e.target.value })} maxLength={256} autoFocus required
                  className={`${field} mt-1 max-lg:min-h-10`} style={fieldStyle} />
              </label>
              {!openPr && (
                <>
                  <label className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
                    Description
                    <textarea value={drafts.body} onChange={(e) => set({ body: e.target.value })} rows={9} maxLength={60000}
                      className={`${field} mt-1 font-[inherit] leading-relaxed`} style={fieldStyle} />
                  </label>
                  <label className="flex items-center gap-2 text-sm max-lg:min-h-10" style={{ color: 'var(--color-text-2)' }}>
                    <input type="checkbox" checked={drafts.draft} onChange={(e) => set({ draft: e.target.checked })} className="h-4 w-4" />
                    Open as a draft
                  </label>
                </>
              )}
              {openPr && drafts.replies.length > 0 && (
                <RepliesEditor t={t} replies={drafts.replies} onChange={(replies) => set({ replies })}
                  hint="Posted on the pull request under your name once the update is pushed; untick the ones not to post." />
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Button type="submit" size="sm" className="max-lg:min-h-10" disabled={!drafts.title.trim() || /[\r\n]/.test(drafts.title) || pending}>
                  {openPr ? (pending ? 'Pushing the update…' : keptReplies(drafts.replies).length ? 'Push the update and post the replies' : 'Push the update') : pending ? `Pushing and opening the ${w.short}…` : drafts.draft ? 'Push and open as a draft' : `Push and open the ${w.short}`}
                </Button>
                <Button type="button" size="sm" variant="ghost" className="max-lg:min-h-10" onClick={cancel} disabled={pending}>Cancel</Button>
              </div>
            </>
          )}
          {mode === 'replies' && (
            <>
              <RepliesEditor t={t} replies={drafts.replies} onChange={(replies) => set({ replies })}
                hint="Posted on the pull request under your name, without pushing anything: the change stays here to decide on." />
              <div className="flex flex-wrap items-center gap-2">
                <Button type="submit" size="sm" className="max-lg:min-h-10" disabled={!keptReplies(drafts.replies).length || pending}>{pending ? 'Posting the replies…' : 'Post the replies'}</Button>
                <Button type="button" size="sm" variant="ghost" className="max-lg:min-h-10" onClick={cancel} disabled={pending}>Cancel</Button>
              </div>
            </>
          )}
          {mode === 'changes' && (
            <Form label="What to change" value={drafts.note} onChange={(v) => set({ note: v })} pending={pending} valid={!!drafts.note.trim()} onCancel={cancel}
              submit="Send to the session" pendingLabel="Sending to the session…"
              placeholder="For example, a test to add, a name to change, a file to leave alone."
              hint="Your note goes to the ticket's session, which works on its branch again and brings a new version of the change here. Nothing is pushed." />
          )}
          {mode === 'reassign' && (
            <Form label="Handover comment" value={drafts.handover} onChange={(v) => set({ handover: v })} pending={pending}
              valid={!!drafts.login && !!drafts.handover.trim()} onCancel={cancel}
              submit={drafts.login ? `Reassign to ${drafts.login}` : 'Reassign'} pendingLabel="Reassigning…" autoFocus={false}
              hint="aidev assigns your teammate, posts this comment under your name, then removes you from the ticket.">
              <Assignee assignees={d.assignees} suggestions={d.suggestions} value={drafts.login} onChange={(v) => set({ login: v })} />
            </Form>
          )}
          {mode === 'reply' && (
            <Form label="Reply" value={drafts.reply} onChange={(v) => set({ reply: v })} pending={pending} valid={!!drafts.reply.trim()} onCancel={cancel}
              submit="Post the reply" pendingLabel="Posting the reply…"
              placeholder={`For example, answer the ${epic ? 'breakdown' : 'plan'}'s open questions, or say what to change.`}
              alt={{ label: 'Post and update the plan', pendingLabel: 'Posting and updating the plan…', active: updating, onPick: (alt) => { updatePlan.current = alt; } }}
              hint={`Posted on the ${gitlabTicket ? 'GitLab issue' : epic ? 'epic' : 'ticket'} under your name. Nothing is decided: the ${epic ? 'breakdown' : 'plan'} and its actions stay. Post and update the plan also asks the session to redo it with your reply${gitlabTicket ? ', once the issue is screened again' : ''}.`} />
          )}
        </form>
      )}
      <DecideError d={d} />
    </div>
  );
}

/** The replies to post, one textarea each under the comment it answers, to edit or untick */
function RepliesEditor({ t, replies, onChange, hint }: { t: Ticket; replies: ReplyDraft[]; onChange: (replies: ReplyDraft[]) => void; hint: string }) {
  const by = feedbackByThread(t.feedback);
  const update = (i: number, patch: Partial<ReplyDraft>) => onChange(replies.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <fieldset className="space-y-3">
      <legend className="text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>Replies to the pull request&apos;s comments</legend>
      <p className="max-w-[72ch] text-xs" style={{ color: 'var(--color-text-3)' }}>{hint}</p>
      {replies.map((r, i) => {
        const f = by.get(r.to);
        const place = f ? feedbackPlace(f) : `thread ${r.to}`;
        return (
          <div key={r.to} className="space-y-1">
            <label className="flex items-center gap-2 text-xs max-lg:min-h-10" style={{ color: 'var(--color-text-2)' }}>
              <input type="checkbox" checked={r.keep} onChange={(e) => update(i, { keep: e.target.checked })} className="h-4 w-4" />
              Reply to {place}
            </label>
            {f && <blockquote className="border-l-2 pl-2" style={{ borderColor: 'var(--color-border-2)' }}><PlainText text={f.excerpt} className="text-xs" color="var(--color-text-3)" /></blockquote>}
            <textarea value={r.body} onChange={(e) => update(i, { body: e.target.value })} rows={2} maxLength={2000} disabled={!r.keep} aria-label={`Your reply to ${place}`}
              className={`${field} font-[inherit] leading-relaxed disabled:opacity-60`} style={fieldStyle} />
          </div>
        );
      })}
    </fieldset>
  );
}

function Outcome({ t, note }: { t: Ticket; note?: string }) {
  const link = (text: string) => t.commentUrl
    ? <a href={t.commentUrl} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>{text}</a>
    : text;
  const line = (color: string, children: React.ReactNode) => <p className="text-sm" style={{ color }}>{children}</p>;
  switch (t.status) {
    case 'implementing': {
      // Not sent to the session (sandboxed, no session): the API says why
      const why = note ?? (t.implementNote && !t.implementNote.startsWith('Sent') ? t.implementNote : undefined);
      if (why) return line('var(--color-warn)', `Approved, not implemented yet. ${why}`);
      if (t.changeErrors?.length) return null;
      return t.change
        ? line('var(--color-success)', `Changes asked for: the session is working on version ${t.change.version + 1} of the change. Nothing is pushed.`)
        : line('var(--color-success)', 'Approved: the session is implementing it on a local branch. Its change comes back here; nothing is pushed.');
    }
    case 'change-ready':
      return note ? <p role="status" className="text-sm" style={{ color: 'var(--color-text-2)' }}>{note}</p> : null;
    case 'pr-opened': {
      const pr = t.pullRequest;
      const w = trackerWords(t.externalRef);
      return line('var(--color-success)', pr
        ? <>{(t.step ?? 1) > 1 ? `Step ${t.step}: ` : ''}<a href={pr.url} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>
          {pr.draft ? `Draft ${w.request}` : w.Request} {w.sigil}{pr.number}<span className="sr-only"> (opens {w.site} in a new tab)</span></a> opened under your name from <code>{pr.branch}</code>
          {pr.behind ? ', behind the default branch (said in its description)' : ''}.</>
        : `${w.Request} opened.`);
    }
    case 'step-done': {
      // The step's pull request is merged or closed (#936): the next step, while the issue is open
      const pr = t.pullRequest;
      const w = trackerWords(t.externalRef);
      return line(pr?.merged ? 'var(--color-success)' : 'var(--color-text-2)', <>
        {`Step ${t.step ?? 1}: `}
        {pr ? <a href={pr.url} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>
          {w.request} {w.sigil}{pr.number}<span className="sr-only"> (opens {w.site} in a new tab)</span></a> : `the ${w.request}`}
        {pr?.merged ? ' merged' : ' closed without merging'}.
        {t.issueState === 'closed' ? ' The issue is closed: nothing more to do.' : note ? ` ${note}` : ''}
      </>);
    }
    case 'dropped':
      return line('var(--color-text-2)', 'Dropped: nothing was pushed. The session\'s branch stays on the server.');
    case 'reassigned':
      return line('var(--color-text-2)', <>Handed to {t.reassignedTo}, with {link('the handover comment')}.</>);
    case 'dismissed':
      return line('var(--color-text-2)', 'Rejected: nothing was done.');
    case 'broken-down': {
      const n = t.subTickets?.length ?? 0;
      return line('var(--color-success)', `Broken down into ${n} sub-ticket${n === 1 ? '' : 's'}, unassigned, attached to the epic.`);
    }
    default:
      // A reply asked for a plan update, or could not ask for it: what happened
      return note ? <p role="status" className="text-sm" style={{ color: 'var(--color-text-2)' }}>{note}</p> : null;
  }
}

const replyDay = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** The operator's replies on the ticket: comments, not decisions, each with its GitHub link */
function ReplyHistory({ t }: { t: Ticket }) {
  const replies = t.history ?? [];
  const w = trackerWords(t.externalRef);
  if (!replies.length) return null;
  return (
    <ul className="space-y-0.5" aria-label={replies.some((h) => h.kind === 'changes') ? 'Your replies and change requests' : 'Your replies'}>
      {replies.map((h, i) => {
        const when = `${h.kind === 'changes' ? 'Asked for changes' : h.kind === 'next-step' ? 'Sent the next step' : h.kind === 'replies' ? `Replied on the ${w.request}` : 'Replied'} on ${replyDay.format(new Date(h.at))}`;
        return (
          <li key={`${h.at}-${i}`} title={h.note ? `${new Date(h.at).toLocaleString('en-GB')}: ${h.note}` : new Date(h.at).toLocaleString('en-GB')}>
            {h.url ? (
              <a href={h.url} target="_blank" rel="noreferrer" className="hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}>
                {when}<span className="sr-only"> (opens {w.site} in a new tab)</span>
              </a>
            ) : when}
            {h.planUpdate ? ', with a plan update' : ''}
          </li>
        );
      })}
    </ul>
  );
}

function edge(t: Ticket): string {
  if (t.progressAlert?.kind === 'blocked' && t.status === 'broken-down') return 'var(--color-warn)';
  if (t.status === 'implementing' && t.changeErrors?.length) return 'var(--color-error)';
  if ((t.status === 'change-ready' || t.status === 'step-done') && t.issueState !== 'closed' && !t.unassignedAt) return 'var(--color-brand)';
  if (t.status === 'implementing' || t.status === 'pr-opened' || (t.status === 'broken-down' && t.issueState !== 'closed' && !t.unassignedAt)) return 'var(--color-success)';
  if (t.status !== 'pending' || t.issueState === 'closed' || t.unassignedAt) return 'var(--color-border-2)';
  return t.proposal ? 'var(--color-brand)' : 'var(--color-error)';
}

/**
 * What the ticket asks (ADR-039 section 7: first on a ticket's page): who asked, since when, and the start of what they
 * wrote, as text; the issue on GitHub has the rest
 */
function Asked({ t }: { t: Ticket }) {
  if (!t.issueBody && !t.issueAuthor) return null;
  return (
    <section aria-label="What is asked" className="space-y-1.5">
      <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>
        Asked{t.issueAuthor ? ` by ${t.issueAuthor}` : ''}{t.issueCreatedAt ? `, ${timeAgo(t.issueCreatedAt)}` : ''}
      </p>
      {t.issueBody && (
        <p className="max-h-48 max-w-[72ch] overflow-y-auto whitespace-pre-wrap break-words rounded-md px-3 py-2 text-sm leading-relaxed"
          tabIndex={0} style={{ color: 'var(--color-text-2)', backgroundColor: 'var(--color-surface-2)' }}>
          {t.issueBody}
        </p>
      )}
    </section>
  );
}

export function TicketCard({ serverId, t, isAdmin, focused, onDecided, titles }: {
  serverId: string; t: Ticket; isAdmin: boolean; focused: boolean; onDecided: (ref: string) => void; titles: Titles;
}) {
  const ref = useRef<HTMLElement>(null);
  const [note, setNote] = useState<string | undefined>();
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView?.({ block: 'center', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [focused]);
  const issue = parseIssueRef(t.externalRef);
  const titleId = `ticket-${t.externalRef}-title`;
  const refId = `ticket-${t.externalRef}-ref`;
  const epic = t.kind === 'epic';
  const noun = epic ? 'epic' : 'ticket';
  const plan = epic ? t.epic : t.proposal;
  const left = t.issueState === 'closed' ? `The ${noun} was closed on ${issue?.gitlab ? 'GitLab' : 'GitHub'}: nothing is left to decide.`
    : t.unassignedAt ? `You are no longer assigned to this ${noun}.` : null;
  const deciding = t.status === 'pending' && isAdmin;
  const actionable = !!plan && t.issueState !== 'closed' && !t.unassignedAt;
  // The change waits on the operator (step 2): ready, or refused by the checks
  const changing = isAdmin && !left && (t.status === 'change-ready' || (t.status === 'implementing' && !!t.changeErrors?.length));
  const seen = useSeen(ref, !(deciding && actionable) || focused);
  const d = useDecision(serverId, t, deciding && actionable, seen || focused, (n) => { setNote(n); onDecided(t.externalRef); });
  const related = (epic ? t.epic?.related : t.proposal?.related) ?? [];
  const shortRef = issue?.key ? `${issue.key} (Jira${t.repo ? `, in ${t.repo.org}/${t.repo.repo}` : ''})`
    : issue?.gitlab ? `${issue.org}#${issue.number} (GitLab)` : issue ? `${issue.org}/${issue.repo} #${issue.number}` : ticketLabel(t.externalRef);
  return (
    <article ref={ref} id={`ticket-${t.externalRef}`} aria-labelledby={`${refId} ${titleId}`} className="card @container min-w-0 px-4 py-4 hover:transform-none sm:px-5"
      style={{ borderLeft: `3px solid ${edge(t)}`, ...(focused ? { outline: '2px solid var(--color-brand-light)', outlineOffset: '3px' } : {}) }}>
      <header className="min-w-0">
        <p id={refId} className="flex flex-wrap items-center gap-x-2 text-xs" style={{ color: 'var(--color-text-3)' }}>
          <span className="break-all">{shortRef}</span>
          {epic && <span className="rounded px-1.5 py-px text-[11px] font-medium" style={{ backgroundColor: 'var(--color-brand-dim)', color: 'var(--color-brand-light)' }}>epic</span>}
        </p>
        <h3 id={titleId} className="mt-1 max-w-[72ch] text-base font-semibold leading-snug" style={{ color: 'var(--color-text)' }}>
          <a href={t.issueUrl ?? issue?.url} target="_blank" rel="noreferrer" className="break-words hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
            {t.issueTitle || shortRef}<span className="sr-only"> (opens {issue?.gitlab ? 'GitLab' : issue?.key ? 'Jira' : 'GitHub'} in a new tab)</span>
          </a>
        </h3>
      </header>
      <div className="mt-4 grid gap-x-10 gap-y-6 text-sm @3xl:grid-cols-[minmax(0,72ch)_minmax(15rem,19rem)] @3xl:justify-between">
        <div className="min-w-0 space-y-5">
          {left && <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>{left}</p>}
          <Asked t={t} />
          {epic ? <EpicPlanView t={t} /> : <Plan t={t} />}
          {epic && <SubTicketResults t={t} />}
          {epic && <EpicProgressView t={t} />}
          <ChangeErrors t={t} />
          <ChangeView t={t} />
          {(t.status === 'pending' || t.status === 'change-ready') && t.error && (
            <p role="alert" className="rounded-md px-3 py-2 text-sm" style={{ backgroundColor: 'var(--color-error-dim)', color: 'var(--color-error)' }}>
              The last decision did not go through: {t.error}
            </p>
          )}
          {(deciding || changing) && <DecisionForm t={t} d={d} />}
          <Outcome t={t} note={note} />
          {isAdmin && !left && t.status === 'step-done' && !note && (
            <NextStepForm serverId={serverId} t={t} onSent={(n) => { setNote(n); onDecided(t.externalRef); }} />
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-5 border-t pt-5 @3xl:sticky @3xl:top-4 @3xl:self-start @3xl:rounded-md @3xl:border @3xl:bg-(--color-surface-2) @3xl:p-4"
          style={{ borderColor: 'var(--color-border)' }}>
          {deciding && <div className="@max-3xl:order-last"><Actions t={t} d={d} actionable={actionable} /></div>}
          {changing && <div className="@max-3xl:order-last"><ChangeActions t={t} d={d} /></div>}
          {(t.status === 'pending' || t.status === 'change-ready') && !isAdmin && (
            <p className="text-xs @max-3xl:order-last" style={{ color: 'var(--color-text-3)' }}>Waiting for an admin of this server to decide.</p>
          )}
          {!epic && <Files files={t.proposal?.files ?? []} />}
          <RelatedPlans serverId={serverId} related={related} titles={titles} pending={t.status === 'pending'} />
          <div className="space-y-1 text-xs" style={{ color: 'var(--color-text-3)' }}>
            <p>{epic ? 'Breakdown' : 'Plan'} ready {timeAgo(t.collectedAt)}.{t.change ? ` Change ready ${timeAgo(t.change.collectedAt)}.` : ''}</p>
            <ReplyHistory t={t} />
            <EarlierSteps t={t} />
            {t.sessionId && (
              <Link to={`/servers/${serverId}/sessions/${t.sessionId}`} className="inline-block py-1 hover:underline focus-visible:outline focus-visible:outline-2 max-lg:min-h-10 max-lg:py-2.5"
                style={{ color: 'var(--color-brand-light)' }}>
                View the {noun} session
              </Link>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

/** The pull requests of the earlier steps of a plan (#936), each with how it ended */
function EarlierSteps({ t }: { t: Ticket }) {
  const earlier = t.earlierPullRequests ?? [];
  const w = trackerWords(t.externalRef);
  if (!earlier.length) return null;
  return (
    <ul className="space-y-0.5" aria-label="Earlier steps">
      {earlier.map((p) => (
        <li key={p.number}>
          {`Step ${p.step}: `}
          <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}>
            {w.sigil}{p.number}<span className="sr-only"> (opens {w.site} in a new tab)</span>
          </a>
          {p.merged ? ', merged' : p.closedAt ? ', closed' : ''}
        </li>
      ))}
    </ul>
  );
}

/** « Implement the next step » (#936): the approach of the next step, sent to the same session */
function NextStepForm({ serverId, t, onSent }: { serverId: string; t: Ticket; onSent: (note?: string) => void }) {
  const [approach, setApproach] = useState('');
  const decide = useDecideTicket(serverId, t.externalRef);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!approach.trim()) return;
    decide.mutate({ decision: 'next-step', approach: approach.trim() }, { onSuccess: (r) => onSent(r.note) });
  };
  return (
    <form onSubmit={submit} className="space-y-3" aria-label="Implement the next step">
      <label className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
        What the next step does
        <textarea value={approach} onChange={(e) => setApproach(e.target.value)} rows={6} maxLength={MAX_TEXT}
          placeholder="For example: implement the ADR that was just accepted, as it says."
          className={`${field} mt-1 font-[inherit] leading-relaxed`} style={fieldStyle} />
      </label>
      <p className="max-w-[72ch] text-xs" style={{ color: 'var(--color-text-2)' }}>
        The same session starts again from the default branch as it is now, on a branch of its own. Its change comes back here; nothing is pushed until you open its {trackerWords(t.externalRef).request}.
      </p>
      <Button type="submit" size="sm" className="max-lg:min-h-10" disabled={!approach.trim() || decide.isPending}>
        {decide.isPending ? 'Sending the next step…' : 'Implement the next step'}
      </Button>
      {decide.error && <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>Not sent: {(decide.error as Error).message}</p>}
    </form>
  );
}

const DECIDED: Record<Exclude<Ticket['status'], 'pending'>, (t: Ticket) => string> = {
  implementing: () => 'approved for implementation',
  'change-ready': () => 'change ready',
  'pr-opened': (t) => {
    const w = trackerWords(t.externalRef);
    return t.pullRequest ? `${w.request} ${w.sigil}${t.pullRequest.number} opened` : `${w.request} opened`;
  },
  'step-done': (t) => `step ${t.step ?? 1} ${t.pullRequest?.merged ? 'merged' : 'closed'}`,
  dropped: () => 'change dropped',
  reassigned: (t) => `handed to ${t.reassignedTo ?? 'a teammate'}`,
  dismissed: () => 'rejected',
  'broken-down': (t) => `broken down into ${t.subTickets?.length ?? 0} sub-tickets`,
};

/** The Reviews tab's tickets: plans to decide first, then the decisions of the last 30 days, folded */
export function TicketPlans({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const { data, error } = useTickets(serverId);
  const [params] = useSearchParams();
  const focus = params.get('ticket');
  const [decidedHere, setDecidedHere] = useState<Set<string>>(() => new Set());
  // Servers without tickets get no empty section; a failed load is said, not hidden as empty
  if (!data?.length && !error) return null;
  const list = data ?? [];
  const titles: Titles = new Map(list.map((t) => [t.externalRef, t.issueTitle]));
  const followed = (t: Ticket) => t.kind === 'epic' && t.status === 'broken-down' && t.issueState !== 'closed' && !t.unassignedAt;
  const working = (t: Ticket) => (t.status === 'implementing' || t.status === 'change-ready' || t.status === 'step-done') && t.issueState !== 'closed' && !t.unassignedAt;
  const open = list.filter((t) => t.status === 'pending' || working(t) || followed(t) || decidedHere.has(t.externalRef));
  const decided = list.filter((t) => !open.includes(t));
  const focusDecided = !!focus && decided.some((t) => t.externalRef === focus);

  return (
    <section aria-labelledby="tickets-title" className="mb-8 min-w-0">
      <h2 id="tickets-title" className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>Tickets</h2>
      <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-3)' }}>
        Issues assigned to you on GitHub, with the plan their session prepared. Nothing is posted until you decide.
      </p>
      <ConsistencyCheck serverId={serverId} isAdmin={isAdmin} titles={titles} />
      {error && (
        <p role="alert" className="mt-3 text-sm" style={{ color: 'var(--color-error)' }}>
          Could not load the tickets: {(error as Error).message}. They refresh every minute.
        </p>
      )}
      {open.length > 0 && (
        <div className="mt-4 space-y-3">
          {open.map((t) => (
            // A new version of the change brings its own title and description to edit
            <TicketCard key={`${t.externalRef} ${t.change?.version ?? 0}`} serverId={serverId} t={t} isAdmin={isAdmin} focused={t.externalRef === focus} titles={titles}
              onDecided={(r) => setDecidedHere((prev) => new Set(prev).add(r))} />
          ))}
        </div>
      )}
      {list.length > 0 && !open.length && <p className="mt-4 text-sm" style={{ color: 'var(--color-text-2)' }}>No ticket plan waits for your decision.</p>}
      {decided.length > 0 && (
        <details className="mt-4" open={focusDecided}>
          <summary className="cursor-pointer py-1.5 text-xs" style={{ color: 'var(--color-text-3)' }}>Decided in the last 30 days ({decided.length})</summary>
          {focusDecided ? (
            <div className="mt-2 space-y-3">
              {decided.filter((t) => t.externalRef === focus).map((t) => (
                <TicketCard key={t.externalRef} serverId={serverId} t={t} isAdmin={isAdmin} focused onDecided={() => undefined} titles={titles} />
              ))}
            </div>
          ) : null}
          <ul className="mt-2 space-y-1">
            {decided.filter((t) => t.externalRef !== focus).map((t) => {
              const issue = parseIssueRef(t.externalRef);
              return (
                <li key={t.externalRef} id={`ticket-${t.externalRef}`} className="break-words text-xs" style={{ color: 'var(--color-text-2)' }}>
                  <a href={t.issueUrl ?? issue?.url} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>
                    {ticketLabel(t.externalRef)}
                  </a>
                  {t.issueTitle ? ` ${t.issueTitle}` : ''}: {DECIDED[t.status as Exclude<Ticket['status'], 'pending'>](t)}
                  {t.decidedAt ? `, ${timeAgo(t.decidedAt)}` : ''}
                </li>
              );
            })}
          </ul>
        </details>
      )}
      {isAdmin && <TrustedPeople serverId={serverId} />}
    </section>
  );
}
